import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter, OnDestroy, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AutoComplete } from '@aileron-web/auto-complete';
import { ErrorNotification, FlightStatusService } from '@modules/flight-status/flight-status.service';
import { AirportList, Cities } from '@modules/flight-status/cities/cities';
import { DatePipe } from '@angular/common';
import { environment } from 'env';
import { validAirportCode } from '@core/validators'
import { AirportService } from '@shared/components';
import { Subject, Subscription, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { DataCollectionClientService } from '@core/services/data-collection-client.service';
import { NearestAirportService } from '@modules/flight-status/cities/nearest-airport.service';

@Component({
  selector: 'hp-cities',
  templateUrl: './cities.component.html',
  styleUrls: ['./cities.component.scss']
})

export class CitiesComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly _cityResultEndPoint = environment.flightStatus.cityResultEndPoint;

    public citiesForm: FormGroup;
    showFormValue = environment.showFormValue;
    submittedForm = '';
    errorCount: number = 0;
    dates: Array<any>;
    errorMessage: ErrorNotification;
    currentDate: Date;
    airportCodesFiltered: string = '[]';
    filterTextChanged: Subject<string> = new Subject<string>();
    subscription: Subscription;
    lastHightlighted: string;
    @ViewChild('originAirport', { read: ElementRef }) originAirport: ElementRef;
    @ViewChild('destinationAirport', { read: ElementRef }) destinationAirport: ElementRef;
    @ViewChild('originAirport') citiesOriginAirport: AutoComplete;
    @ViewChild('destinationAirport') citiesDestinationAirport: AutoComplete;
    @Input() loadingState: boolean;
    @Output() loadingStateChanged: EventEmitter<boolean> = new EventEmitter();

    constructor(
        public _flightStatusService: FlightStatusService,
        public datePipe: DatePipe,
        private _window: Window,
        private _logger: NGXLogger,
        public _airportService: AirportService,
        public _dataCollectionClient: DataCollectionClientService,
        private _nearestAirportService: NearestAirportService
    ) {

        this.citiesForm = new FormGroup({
            originAirport: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(3)], [validAirportCode(_airportService)]),
            destinationAirport: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(3)],[validAirportCode(_airportService)]),
            citiesDate: new FormControl()
            },
        );
    }

    ngOnInit(): void {
        window.addEventListener('keydown', (event) => {
            if(event.key == 'Tab'){
                const origin = document.getElementById('originAirportInput').shadowRoot.querySelector('ul').querySelector('.active');
                const destination = document.getElementById('destinationAirportInput').shadowRoot.querySelector('ul').querySelector('.active');
                if(origin != null){
                    this.lastHightlighted = origin.textContent.trim().substring(0,3);
                }
                else if( destination != null){
                    this.lastHightlighted = destination.textContent.trim().substring(0,3);
                }
                else{
                    this.lastHightlighted = null;
                }

            }
            
            
          }, true);
        this.filterTextChanged
            .subscribe(
                filterQuery => {
                    this.loadData(filterQuery);
                }
            );
    }

    autoSelectAirport(control: string,event): void{
        if(event.target.adcTextInput?.value.length >= 3){
            const lastActive = document.getElementById(control + 'Input').shadowRoot.querySelector('ul').querySelector('.active');
            if(lastActive){
                const airport = (lastActive.textContent.trim().substring(0,3));
                document.getElementById(control + 'Input').shadowRoot.querySelector('adc-text-input').shadowRoot.querySelector('input').value = airport;
                this.citiesForm.controls[control].setValue(airport);
            }
            
        }
    }

    ngAfterViewInit(): void {
        this.setDates(this._flightStatusService.dates);
    }

    submitForm(inputForm: FormGroup, event: Event) {
        if(event.type != 'click')
        {
            return;
        }

        this.loadingState = true;
        this.loadingStateChanged.emit(this.loadingState);
        this.submittedForm = JSON.stringify(inputForm.value);
        this.currentDate = new Date();

        if (inputForm.pending) {
            const sub = inputForm.statusChanges.subscribe((res) => {
                this.checkForm(inputForm);
                sub.unsubscribe();
            });
        } else {
            this.checkForm(inputForm);
        }
    }

    checkForm(inputForm: FormGroup) {
        this._flightStatusService.errorIDs = [];
        if (inputForm.valid) {
            const cities: Cities = {
                originAirport: this.citiesForm.controls.originAirport.value,
                destinationAirport: this.citiesForm.controls.destinationAirport.value,
                travelDate: this.datePipe.transform(this.citiesForm.controls.citiesDate.value, 'EEEE, MMMM d'),
                clientLocalDate: this.datePipe.transform(this.currentDate, 'EEEE, MMMM d'),
            };

            this._flightStatusService.searchFlightsbyCities(cities).subscribe(
                (res) => {
                    if(JSON.stringify(res).includes('\u003e'+ this._flightStatusService.translate('error:ERRCODE408.title') + '\u003c')) {
                        this.loadingState = false;
                        this.loadingStateChanged.emit(this.loadingState);

                        inputForm.controls['originAirport'].setErrors({'incorrect': true});
                        inputForm.controls['destinationAirport'].setErrors({'incorrect': true});
                        this._flightStatusService.setErrorMessages(
                            inputForm.controls['originAirport'],
                            this.citiesOriginAirport,
                            {invalidAirportMessage: { text: this._flightStatusService.translate('error:ERRCODE1269'), code: 'ERRCODE1269'}},
                            );
                        this._flightStatusService.setErrorMessages(
                            inputForm.controls['destinationAirport'],
                            this.citiesDestinationAirport,
                            {invalidAirportMessage: { text: this._flightStatusService.translate('error:ERRCODE1269'), code: 'ERRCODE1269'}},
                            );
                        this.errorCount = this._flightStatusService.errorIDs.length;
                        this.errorMessage = this._flightStatusService.getNotificationMessage(this.errorCount);
                        this.updateDCFData();
                        inputForm.controls['originAirport'].setErrors(null);
                        inputForm.controls['destinationAirport'].setErrors(null);

                    } else {
                        this._window.location.assign(this._cityResultEndPoint);
                    }
                },
                (err) => {
                    this._logger.error(err);
                });

        } else {
            this.loadingState = false;
            this.loadingStateChanged.emit(this.loadingState);
            this._flightStatusService.setErrorMessages(
                inputForm.controls['originAirport'],
                this.citiesOriginAirport,
                {
                    requiredMessage: { text: this._flightStatusService.translate('error:ERRCODE1256'), code: 'ERRCODE1256' },
                    lengthMessage: { text: this._flightStatusService.translate('error:ERRCODE1348'), code: 'ERRCODE1348' },
                    invalidAirportMessage: { text: this._flightStatusService.translate('error:ERRCODE1269'), code: 'ERRCODE1269' }
                });
            this._flightStatusService.setErrorMessages(
                inputForm.controls['destinationAirport'],
                this.citiesDestinationAirport,
                {
                    requiredMessage: { text: this._flightStatusService.translate('error:ERRCODE1257'), code: 'ERRCODE1257' },
                    lengthMessage: { text: this._flightStatusService.translate('error:ERRCODE1348'), code: 'ERRCODE1348' },
                    invalidAirportMessage: { text: this._flightStatusService.translate('error:ERRCODE1269'), code: 'ERRCODE1269' }
                });
            this.errorCount = this._flightStatusService.errorIDs.length;
            this.errorMessage = this._flightStatusService.getNotificationMessage(this.errorCount);
            this.updateDCFData();
        }
    }

    focusFirstError() {
        if (this.citiesForm.controls['originAirport'].errors) {
            this.originAirport.nativeElement.shadowRoot.querySelector('adc-text-input').shadowRoot.querySelector('input').focus();
        } else if (this.citiesForm.controls['destinationAirport'].errors) {
            this.destinationAirport.nativeElement.shadowRoot.querySelector('adc-text-input').shadowRoot.querySelector('input').focus();
        }
    }

    setDates(dates: Array<any>) {
        if (dates) {
            this.dates = dates.slice();
            this.citiesForm.controls['citiesDate'].setValue(this.dates[2].value);
        }
    }

    /**
     * @param event triggered once the user starts typing some characters
     * in the airport field. Will use AirportService to query and return
     * airport results based on the search text.
     */
    filterAirports(event): void {
        const path = event.path || (event.composedPath && event.composedPath());
        if (path[0].matches('button')) {
            return;
        }
        if (event.target.adcTextInput.value && event.keyCode !== 16) {
            if (event.target.adcTextInput.value.length >= 3) {
                this.filterTextChanged.next(event.target.adcTextInput.value);
            } else {
                this.airportCodesFiltered = JSON.stringify([{
                    'value': event.target.adcTextInput.value
                }]);
            }
        } else {
            this.airportCodesFiltered = '[]';
        }
    }

    loadData(filterQuery: string = null) {
        this.subscription = this._airportService.searchAirports(filterQuery).pipe(catchError(err => {
            this._logger.error(err);
            return throwError(err);
        })).subscribe((response: AirportList) => {
            this.filterResponse(Object.assign([], response));
        });
    }

    filterResponse(airportList: Array<AirportList>): void {
        airportList.map((element) => {
            this.displayName(element);
        });
        this.airportCodesFiltered = JSON.stringify(airportList);
    }

    displayName(item: AirportList) {
        const location = item.stateCode != null && item.stateCode != '' ? item.stateCode : item.countryName;
        item.displayName = item.code + ' - ' + item.name + ', ' + location;
        item.selectionName = item.code;
        return item;
    }

    ngOnDestroy(): void {
        // unsubscribe to ensure no memory leaks
        this.subscription?.unsubscribe();
    }

    /*
     * Only allows letters to be typed, no special characters or numbers
     * If tab is pressed, search bar is automatically filled with the last highlighted airport
     */
    keyPressFilter(control, event) {
        if(event.keyCode===9 && this.lastHightlighted){
            document.getElementById(control + 'Input').shadowRoot.querySelector('adc-text-input').shadowRoot.querySelector('input').value = this.lastHightlighted;
            this.citiesForm.controls[control].setValue(this.lastHightlighted);
        }
        const charCode = (event.which) ? event.which : event.keyCode;
        return (charCode > 64 && charCode < 91) || (charCode > 96 && charCode < 123) || (charCode < 33);
    }

    updateDCFData() {
        const errorDCFData = {
            page_name: 'Flight Status Webcomponent',
            channel: 'Homepage',
            page_action: 'Cities Form Submit',
            error_id: this._flightStatusService.errorIDs.toString()
        };
        this._dataCollectionClient.updateAnalytics(errorDCFData, 'Error Call', 'link');
    }

    locate(control): void {
        const self = this;
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(getNearestAirport, undefined, { timeout: 10000 });
        }

        function getNearestAirport(pos) {
            self._nearestAirportService.searchForNearestAirport(pos).subscribe(res => {
                    document.getElementById(control + 'Input').shadowRoot.querySelector('adc-text-input').shadowRoot.querySelector('input').value = res.code
                    self.citiesForm.controls[control].setValue(res.code)
                },
                error => {
                    self._logger.error('Error: getNearestAirport() ' + error)
                });
        }
    }
}
